import {
  Box,
  Button,
  InputAdornment,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@mui/material'
import React from 'react'
import { RadioCard } from '../RadioCard'
import { GigIcon } from '../GigIcon'
import { ButtonTab, ButtonTabs } from '../ButtonTabs'
import { Location } from '../Icon/Location'
import ExpandMore from '@mui/icons-material/ExpandMore'
import {
  isValidZip,
  useConnectModuleFormReturnType,
} from '../../containers/PageSectionConnectModule/useConnectModuleForm'
import { CONNECT_MODULE_SKILL_GROUPS } from '../../gigproApi/skillGroups'
import { getLabelForSkillGroup } from '../../utils/skill'

export type Role = (typeof roles)[number]
const roles = ['Business', 'Pro'] as const

export const ConnectModuleForm = ({
  role,
  setRole,
  handleSubmit,
  zip,
  setZip,
  submitted,
  selectedSkillGroup,
  setSelectedSkillGroup,
  error,
}: useConnectModuleFormReturnType) => {
  return (
    <Box
      component="form"
      onSubmit={(e) => {
        e.preventDefault()
        handleSubmit()
      }}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
        position: 'relative',
      }}
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
        <Box
          sx={{
            padding: 0.5,
            borderRadius: '4px',
            backgroundColor: ({ palette }) => palette.background.paper,
          }}
        >
          <ButtonTabs
            variant="fullWidth"
            indicatorColor="secondary"
            value={roles.findIndex((r) => r === role)}
            onChange={(e, newValue) => setRole(roles[newValue])}
          >
            {roles.map((r) => (
              <ButtonTab key={r} label={`I'm a ${r}`} />
            ))}
          </ButtonTabs>
        </Box>
        <Box
          sx={{
            width: '100%',
            flexDirection: 'column',
            gap: 0.5,
            display: { sm: 'none', xs: 'flex' },
          }}
        >
          <Typography variant="caption1">
            {role === 'Business'
              ? 'I’m a BUSINESS looking for a..'
              : 'I’m a PRO looking for Gigs as a..'}
          </Typography>
          <Select
            IconComponent={ExpandMore}
            value={selectedSkillGroup.id}
            sx={{
              backgroundColor: ({ palette }) => palette.background.paper,
              borderRadius: '4px',
            }}
            color="secondary"
            size="small"
            onChange={(e) => {
              const newSkillGroup = CONNECT_MODULE_SKILL_GROUPS.find(
                (skillGroup) => skillGroup.id === e.target.value,
              )
              if (!newSkillGroup) {
                console.error('Could not find skill group.')
                return null
              }
              setSelectedSkillGroup(newSkillGroup)
            }}
          >
            {CONNECT_MODULE_SKILL_GROUPS.map((group) => (
              <MenuItem
                key={group.id}
                id={group.id}
                className="connect-module-skill-select"
                value={group.id}
              >
                {getLabelForSkillGroup(group)}
              </MenuItem>
            ))}
          </Select>
        </Box>
        <TextField
          sx={{
            '.MuiInputBase-root': {
              backgroundColor: (theme) => theme.palette.background.paper,
            },
          }}
          autoComplete="postal-code"
          value={zip}
          onChange={(e) => setZip(e.target.value)}
          helperText={
            submitted && !isValidZip(zip) && 'Please enter a valid postal code.'
          }
          error={submitted && !isValidZip(zip)}
          slotProps={{
            input: {
              startAdornment: (
                <InputAdornment position="start">
                  <Location color="secondary" />
                </InputAdornment>
              ),
            },
          }}
          variant="outlined"
          placeholder="Enter postal code"
          color="secondary"
          size="small"
        />
        <Box
          sx={{
            gap: 2,

            display: { xs: 'none', sm: 'grid' },

            gridTemplateColumns: {
              xs: 'repeat(2, minmax(130px, 1fr))',
              sm: 'repeat(4, minmax(130px, 1fr))',
              md: 'repeat(5, minmax(130px, 1fr))',
            },
          }}
        >
          {CONNECT_MODULE_SKILL_GROUPS.map((group) => (
            <RadioCard
              key={group.id}
              id={group.id}
              selected={selectedSkillGroup?.id === group.id}
              data-skill={group.id}
              className="connect-module-skill-select"
              onClick={() => {
                setSelectedSkillGroup(group)
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                }}
              >
                <GigIcon skillGroup={group.id} size={40} />
                <Typography align="center">
                  {getLabelForSkillGroup(group)}
                </Typography>
              </Box>
            </RadioCard>
          ))}
        </Box>
      </Box>
      <Button type="submit" size="small" variant="contained">
        Find {role === 'Business' ? 'Pros' : 'Gigs'}
      </Button>
      {!!error && (
        <Box sx={{ display: 'flex', justifyContent: 'center' }}>
          <Typography
            align="center"
            variant="caption1"
            sx={{ color: 'error.main' }}
          >
            {error}
          </Typography>
        </Box>
      )}
    </Box>
  )
}
